import type { CoverEffect } from 'fym-shared/src/music24/coverDoc';
import { seededRandom } from '../seededRandom';
import { effectColour, effectParams, hexToRgb, luminance, rgba } from './effectParams';

type Ctx = CanvasRenderingContext2D;

/** The largest side of the work copy of the pixel effects (duotone, halftone): fast at 3000 px too. */
const WORK_SIDE = 1024;
const NOISE_SIDE = 256;

function makeCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

let noiseTile: HTMLCanvasElement | null = null;

/** One tile of grey noise, made once. The seed is fixed, so every draw shows the same grain. */
function grainTile(): HTMLCanvasElement {
  if (noiseTile !== null) {
    return noiseTile;
  }
  const tile = makeCanvas(NOISE_SIDE, NOISE_SIDE);
  const tileCtx = tile.getContext('2d');
  if (tileCtx !== null) {
    const image = tileCtx.createImageData(NOISE_SIDE, NOISE_SIDE);
    const random = seededRandom('m24-cover-grain');
    for (let index = 0; index < image.data.length; index += 4) {
      const value = Math.round(random() * 255);
      image.data[index] = value;
      image.data[index + 1] = value;
      image.data[index + 2] = value;
      image.data[index + 3] = 255;
    }
    tileCtx.putImageData(image, 0, 0);
  }
  noiseTile = tile;
  return tile;
}

function drawGrain(ctx: Ctx, side: number, effect: CoverEffect): void {
  const { alpha, cellShare } = effectParams.grain(effect.strength);
  const pattern = ctx.createPattern(grainTile(), 'repeat');
  if (pattern === null) {
    return;
  }
  // One noise pixel is `cellShare` of the side, at every size of the cover.
  const scale = Math.max(0.25, cellShare * side);
  ctx.globalCompositeOperation = 'overlay';
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = false;
  ctx.scale(scale, scale);
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, side / scale, side / scale);
}

function drawVignette(ctx: Ctx, side: number, effect: CoverEffect): void {
  const { alpha, innerShare } = effectParams.vignette(effect.strength);
  const radius = side * 0.75;
  const gradient = ctx.createRadialGradient(side / 2, side / 2, radius * innerShare, side / 2, side / 2, radius);
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
  gradient.addColorStop(1, `rgba(0, 0, 0, ${alpha})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, side, side);
}

function drawCircles(ctx: Ctx, side: number, effect: CoverEffect): void {
  const { rings, gapShare, lineShare, alpha } = effectParams.circles(effect.strength);
  const random = seededRandom(effect.id);
  const centreX = (0.25 + random() * 0.5) * side;
  const centreY = (0.2 + random() * 0.4) * side;
  // A soft disc in the second colour, then the rings in the first.
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = rgba(effectColour(effect, 1, '#6417fb'), alpha * 0.45);
  ctx.beginPath();
  ctx.arc(
    (0.2 + random() * 0.6) * side,
    (0.55 + random() * 0.3) * side,
    side * (0.16 + random() * 0.12),
    0,
    Math.PI * 2
  );
  ctx.fill();
  ctx.globalCompositeOperation = 'soft-light';
  ctx.strokeStyle = rgba(effectColour(effect, 0, '#ffffff'), alpha);
  ctx.lineWidth = Math.max(1, lineShare * side);
  for (let ring = 1; ring <= rings; ring += 1) {
    ctx.beginPath();
    ctx.arc(centreX, centreY, ring * gapShare * side, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function roundedRect(ctx: Ctx, x: number, y: number, size: number, radius: number): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + size, y, x + size, y + size, radius);
  ctx.arcTo(x + size, y + size, x, y + size, radius);
  ctx.arcTo(x, y + size, x, y, radius);
  ctx.arcTo(x, y, x + size, y, radius);
  ctx.closePath();
}

function drawNeonFrame(ctx: Ctx, side: number, effect: CoverEffect): void {
  const params = effectParams.neonFrame(effect.strength);
  const colour = effectColour(effect, 0, '#ff3ea5');
  const inset = params.insetShare * side;
  ctx.globalCompositeOperation = 'screen';
  ctx.globalAlpha = params.alpha;
  ctx.strokeStyle = colour;
  ctx.lineWidth = Math.max(1, params.lineShare * side);
  ctx.shadowColor = colour;
  ctx.shadowBlur = params.glowShare * side;
  roundedRect(ctx, inset, inset, side - inset * 2, params.radiusShare * side);
  // Two strokes: the tube and its glow.
  ctx.stroke();
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.lineWidth = Math.max(1, params.lineShare * side * 0.35);
  ctx.stroke();
}

function drawLightLeak(ctx: Ctx, side: number, effect: CoverEffect): void {
  const { alpha, radiusShare } = effectParams.lightLeak(effect.strength);
  const random = seededRandom(effect.id);
  const corners: Array<[number, number]> = [
    [random() < 0.5 ? 0 : side, 0],
    [random() < 0.5 ? 0 : side, side],
  ];
  ctx.globalCompositeOperation = 'screen';
  corners.forEach(([x, y], index) => {
    const colour = effectColour(effect, index, index === 0 ? '#ff963c' : '#ff5a3c');
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radiusShare * side);
    gradient.addColorStop(0, rgba(colour, alpha * (index === 0 ? 0.9 : 0.55)));
    gradient.addColorStop(0.45, rgba(colour, alpha * 0.3));
    gradient.addColorStop(1, rgba(colour, 0));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, side, side);
  });
}

/** A copy of the cover as it is now, at most `WORK_SIDE` pixels wide, with its pixels. */
function workCopy(ctx: Ctx, side: number, workSide: number): { canvas: HTMLCanvasElement; image: ImageData } | null {
  const canvas = makeCanvas(workSide, workSide);
  const workCtx = canvas.getContext('2d', { willReadFrequently: true });
  if (workCtx === null) {
    return null;
  }
  workCtx.drawImage(ctx.canvas, 0, 0, side, side, 0, 0, canvas.width, canvas.height);
  try {
    return { canvas, image: workCtx.getImageData(0, 0, canvas.width, canvas.height) };
  } catch (error) {
    // A picture from a host with no CORS headers taints the canvas: the pixel effects cannot read it.
    console.warn('Cover effect: the pixels of the cover cannot be read', error);
    return null;
  }
}

function drawHalftone(ctx: Ctx, side: number, effect: CoverEffect): void {
  const { cellShare, alpha } = effectParams.halftone(effect.strength);
  const cells = Math.max(8, Math.round(1 / cellShare));
  const copy = workCopy(ctx, side, cells);
  if (copy === null) {
    return;
  }
  const cell = side / cells;
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
  ctx.beginPath();
  for (let row = 0; row < cells; row += 1) {
    for (let column = 0; column < cells; column += 1) {
      const at = (row * cells + column) * 4;
      const dark = 1 - luminance(copy.image.data[at] ?? 0, copy.image.data[at + 1] ?? 0, copy.image.data[at + 2] ?? 0);
      const radius = cell * 0.5 * Math.sqrt(dark) * 0.95;
      if (radius > 0.2) {
        const x = (column + 0.5) * cell;
        const y = (row + 0.5) * cell;
        ctx.moveTo(x + radius, y);
        ctx.arc(x, y, radius, 0, Math.PI * 2);
      }
    }
  }
  ctx.fill();
}

function drawDuotone(ctx: Ctx, side: number, effect: CoverEffect): void {
  const { alpha } = effectParams.duotone(effect.strength);
  const copy = workCopy(ctx, side, Math.min(WORK_SIDE, side));
  if (copy === null) {
    return;
  }
  const dark = hexToRgb(effectColour(effect, 0, '#6417fb'));
  const light = hexToRgb(effectColour(effect, 1, '#ffd3de'));
  const { data } = copy.image;
  for (let index = 0; index < data.length; index += 4) {
    const share = luminance(data[index] ?? 0, data[index + 1] ?? 0, data[index + 2] ?? 0);
    data[index] = dark[0] + (light[0] - dark[0]) * share;
    data[index + 1] = dark[1] + (light[1] - dark[1]) * share;
    data[index + 2] = dark[2] + (light[2] - dark[2]) * share;
  }
  copy.canvas.getContext('2d')?.putImageData(copy.image, 0, 0);
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(copy.canvas, 0, 0, side, side);
}

/** A blur with no `ctx.filter` (Safari): the cover is drawn small and then large again, a few times. */
function drawBlur(ctx: Ctx, side: number, effect: CoverEffect): void {
  const { factor, passes } = effectParams.blur(effect.strength);
  if (factor <= 1.05) {
    return;
  }
  const small = Math.max(8, side / factor);
  const down = makeCanvas(small, small);
  const downCtx = down.getContext('2d');
  if (downCtx === null) {
    return;
  }
  downCtx.imageSmoothingEnabled = true;
  downCtx.imageSmoothingQuality = 'high';
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  for (let pass = 0; pass < passes; pass += 1) {
    downCtx.clearRect(0, 0, down.width, down.height);
    downCtx.drawImage(ctx.canvas, 0, 0, side, side, 0, 0, down.width, down.height);
    // A little larger than the square, so the edges do not fade to the ground.
    const bleed = side * 0.01;
    ctx.drawImage(down, -bleed, -bleed, side + bleed * 2, side + bleed * 2);
  }
}

const DRAWERS: Readonly<Record<CoverEffect['type'], (ctx: Ctx, side: number, effect: CoverEffect) => void>> = {
  grain: drawGrain,
  vignette: drawVignette,
  circles: drawCircles,
  neonFrame: drawNeonFrame,
  lightLeak: drawLightLeak,
  halftone: drawHalftone,
  duotone: drawDuotone,
  blur: drawBlur,
};

/**
 * Draws one effect over what the cover holds now, on a square of `side` pixels. Plain canvas
 * operations only (no `ctx.filter`), with every size as a share of the side and every random
 * number from the id of the effect: the screen and the exported file are the same picture.
 * To add an effect: add its type and defaults in fym-shared `coverDoc.ts`, its numbers in
 * `effectParams.ts`, and its drawer here.
 */
export function drawCoverEffect(ctx: Ctx, effect: CoverEffect, side: number): void {
  if (effect.strength <= 0) {
    return;
  }
  ctx.save();
  DRAWERS[effect.type](ctx, side, effect);
  ctx.restore();
}
